import React from 'react';
import { View, Text } from 'react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import {
  MapPin,
  FolderOpen,
  Star,
  ChevronRight,
  CheckSquare,
  Square,
  Bookmark,
  BookmarkCheck,
  Scale,
  Eye,
  FileSearch,
  KeyRound,
  RotateCcw,
} from 'lucide-react-native';
import type { LucideIcon } from 'lucide-react-native';
import Animated, { FadeIn } from 'react-native-reanimated';
import { IRSScorePill } from '@/components/score/atoms/IRSScorePill';
import { CardActionButton } from './CardActionButton';
import { CardExpandChevron } from './CardExpandChevron';
import { useThemeColors } from '@/lib/useThemeColors';
import type { DealflowStartup } from '@/lib/state/dealflow-store';
import { formatCurrency } from '@/lib/utils/format';
import { webStyle } from '@/lib/platform';
import { useResponsive } from '@/lib/useResponsive';
import { StatusBadge } from '@/components/ui';
import { STAGE_LABELS, type MatchScore } from '@/lib/state/marketplace';
import type { ListLayoutType } from '@/components/ListLayout';
import { CompanyAvatar } from '@/components/CompanyAvatar';
import type { AnalyticsEventName } from '@/lib/analytics';
import { offeredActions, actionTarget, dataRoomOpen, type DealAction, type DealStage } from '@/lib/dealflow/stage';
import type { DataRoomRequestState } from '@/lib/dd/dd-entry';

/**
 * The stage's actions, minus one that cannot succeed.
 *
 * `request-access` is dropped the moment a request row exists. The write is
 * keyed UNIQUE (startup_id, investor_profile_id), so a second attempt takes the
 * upsert's ON CONFLICT DO UPDATE branch — and the investor has no UPDATE policy
 * on `data_room_access_requests`; only the startup side does. Offering it again
 * offered a guaranteed error (INVERIO-APP-P/-Q/-X/-Y).
 *
 * Everything else the stage offers is untouched: this is one impossible action
 * removed, not a second opinion about what a stage means.
 */
function visibleActionsFor(
  stage: DealStage,
  requestState: DataRoomRequestState,
): readonly DealAction[] {
  if (requestState === 'none') return offeredActions(stage);
  return offeredActions(stage).filter((a) => a !== 'request-access');
}

/**
 * Says where the request has got to, in the space the button used to occupy.
 *
 * Red for a decline, because it is an answer and not a delay. There is no
 * re-request affordance here by design — a rejection is final; the investor
 * reaches the founder through the conversation instead (offered on the startup
 * detail screen, which has the profile ids the thread needs).
 */
function AccessStatePill({
  state,
  colors,
}: {
  state: Exclude<DataRoomRequestState, 'none'>;
  colors: ReturnType<typeof useThemeColors>;
}) {
  const label =
    state === 'pending' ? 'Access requested' : state === 'approved' ? 'Access granted' : 'Access rejected';
  const tint =
    state === 'pending' ? colors.text.tertiary : state === 'approved' ? colors.success : colors.error;
  return (
    <View
      className="px-3 py-1.5 rounded-full shrink-0"
      style={{ backgroundColor: colors.surface.secondary, borderWidth: 1, borderColor: tint }}
    >
      <Text numberOfLines={1} className="text-xs font-semibold" style={{ color: tint }}>
        {label}
      </Text>
    </View>
  );
}
import type { DealStatusConfig } from './deal-status-badges';
import { CARD_ACTION_PRESENTATION, type CardActionIcon } from './dealflow-card-actions';

// Resolves a `CardActionIcon` name (data) to the `LucideIcon` component
// (presentation) `CardActionButton` renders. Kept local to this file — see
// `dealflow-card-actions.ts` for why the presentation map itself cannot hold
// components.
const CARD_ACTION_ICONS: Record<CardActionIcon, LucideIcon> = {
  'chevron-right': ChevronRight,
  eye: Eye,
  'file-search': FileSearch,
  'key-round': KeyRound,
  'rotate-ccw': RotateCcw,
  star: Star,
};

interface CommitmentInfo {
  type: 'soft' | 'hard';
  status: 'active' | 'converted' | string;
  amount: number;
}

interface DealflowCardProps {
  deal: DealflowStartup;
  index: number;
  layout: ListLayoutType;
  matchScore?: MatchScore | null;
  statusConfig: DealStatusConfig;
  /** Whether the card is expanded (list layout only) */
  isExpanded: boolean;
  onToggleExpanded: (id: string) => void;
  /** Selection mode */
  isSelectionMode: boolean;
  isSelected: boolean;
  onToggleSelection: (id: string) => void;
  onEnterSelectionMode: () => void;
  /** Comparison */
  isInComparison: boolean;
  canAddMore: boolean;
  onAddToComparison: (id: string) => void;
  onRemoveFromComparison: (id: string) => void;
  /** Saved items */
  isSaved: boolean;
  onSave: (type: 'startup', id: string) => void;
  onUnsave: (id: string) => void;
  /** Actions */
  onViewStartup: (dealId: string) => void;
  onViewScore: (dealId: string, e?: { stopPropagation: () => void }) => void;
  /**
   * Move this deal from one stage to another. `from` is passed because the card
   * is the only thing here that knows where the deal currently is, and a
   * rejected server write has to put it back exactly there — see
   * `lib/dealflow/use-change-stage.ts`. A handler that inferred `from` would be
   * inferring the rollback target.
   */
  onUpdateStatus: (dealId: string, from: DealStage, to: DealStage) => void;
  onTrack: (event: AnalyticsEventName, props?: Record<string, unknown>) => void;
  /**
   * Due-diligence entry state for this deal, or null if the investor has not
   * started DD on it. Entering DD is the explicit act of requesting data-room
   * access — see `lib/dd/dd-entry.ts`.
   */
  /**
   * The investor's own request state for this startup, from
   * `requestStateFor()` — stage-blind, read from the request rows.
   *
   * Replaces a `ddState` prop that was declared, destructured, and then never
   * rendered. That is precisely why this card kept offering Request access
   * after a request existed: nothing on it reflected one. Re-requesting is
   * refused by RLS (INVERIO-APP-P/-Q/-X/-Y), so the offer had to go.
   */
  requestState?: DataRoomRequestState;
  /** Open the existing review (`open-diligence`). Navigation only. */
  onDDAction: (dealId: string) => void;
  /**
   * `request-access` — ask the founder to open their data room AND move the
   * deal to `awaiting_access`. It gets its own prop rather than falling through
   * to `onUpdateStatus` because it is not a stage change: a stage move with no
   * request row strands the deal at `awaiting_access` with nobody asked, and
   * `awaiting_access` deliberately offers no way to re-request.
   */
  onRequestAccess: (dealId: string) => void;
  /**
   * True while THIS deal's access request is in flight — dims and refuses the
   * button so the round trip is visible and not double-tappable.
   */
  isRequestingAccess?: boolean;
  /** Commitment info */
  myCommitment?: CommitmentInfo | null;
  /** Privacy mask for investor's financial values */
  isMasked?: boolean;
}

export function DealflowCard({
  deal,
  index,
  layout,
  matchScore,
  statusConfig,
  isExpanded,
  onToggleExpanded,
  isSelectionMode,
  isSelected,
  onToggleSelection,
  onEnterSelectionMode,
  isInComparison,
  canAddMore,
  onAddToComparison,
  onRemoveFromComparison,
  isSaved,
  onSave,
  onUnsave,
  onViewStartup,
  onViewScore,
  onUpdateStatus,
  onTrack,
  requestState = 'none',
  onDDAction,
  onRequestAccess,
  isRequestingAccess = false,
  myCommitment,
  isMasked = false,
}: DealflowCardProps) {
  const colors = useThemeColors();
  const { isDesktop } = useResponsive();
  // NOT a widening of DealStatusConfig — that map stays TOTAL over `DealStage`,
  // so a new stage without a badge is still a compile error in every screen
  // (see deal-status-badges.ts). This guards the other direction: `deal.status`
  // is rehydrated from the persisted `inverio-dealflow` store, so a stage string
  // written by an OLDER build can arrive at a map that no longer has that key.
  // Compile-time totality cannot see rehydrated data.
  const status = statusConfig[deal.status] ??
    { color: colors.text.tertiary, bg: colors.light[300], label: 'Unknown' };

  /**
   * THE handler for every derived action button, in BOTH layouts.
   *
   * It is one function because it was two. The list and grid layouts each had
   * their own copy of this if-chain, and when `request-access` was added — an
   * action that must NOT fall through to `onUpdateStatus`, because a stage move
   * without a request row strands the deal at `awaiting_access` with nobody
   * asked — only the grid copy got it. The list layout is the DEFAULT
   * (`useListLayout('list')`), so the bug was live on the ordinary path while
   * the fix looked done.
   *
   * Add a new action to `stage.ts`'s ACTIONS table and handle it HERE. Anything
   * that is not explicitly named falls through to a plain stage change, so an
   * action needing extra writes must be named or it will silently do half its
   * job. Guarded by ARCH-12 in scripts/ci/grep-guards.sh.
   */
  const runAction = (action: DealAction) => {
    if (action === 'view') { onViewStartup(deal.id); return; }
    if (action === 'open-diligence') { onDDAction(deal.id); return; }
    // NOT a plain stage change — it must write the founder's request row too.
    if (action === 'request-access') { onRequestAccess(deal.id); return; }
    const target = actionTarget(action);
    if (target === null) return;
    onUpdateStatus(deal.id, deal.status, target);
    onTrack('dealflow_status_changed', { startupId: deal.id, newStatus: target });
  };

  const commitmentBadge = myCommitment ? (
    <View
      className="px-2 py-0.5 rounded-full"
      style={myCommitment.type === 'hard' || myCommitment.status === 'converted'
        ? { backgroundColor: colors.primary[500] }
        : { borderWidth: 1, borderColor: colors.primary[500] }
      }
    >
      <Text className="text-[10px] font-semibold" style={{
        color: myCommitment.type === 'hard' || myCommitment.status === 'converted'
          ? '#FFFFFF'
          : colors.primary[500]
      }}>
        {myCommitment.type === 'hard' || myCommitment.status === 'converted'
          ? `Committed: ${isMasked ? '••••••' : formatCurrency(myCommitment.amount)}`
          : `Soft: ${isMasked ? '••••••' : formatCurrency(myCommitment.amount)}`}
      </Text>
    </View>
  ) : null;

  const selectionCheckbox = isSelectionMode ? (
    <AnimatedPressable
      onPress={() => onToggleSelection(deal.id)}
      aria-label={`Select ${deal.startup.company.name}`}
      accessibilityState={{ checked: isSelected }}
      className="mr-3"
    >
      {isSelected ? (
        <CheckSquare size={20} color={colors.primary[600]} />
      ) : (
        <Square size={20} color={colors.text.tertiary} />
      )}
    </AnimatedPressable>
  ) : null;

  const scoreButton = (size: 'sm' | 'md' = 'sm') => (
    <AnimatedPressable
      onPress={(e) => onViewScore(deal.id, e)}
      aria-label={`View score details for ${deal.startup.company.name}, score ${deal.readinessScore}`}
    >
      <IRSScorePill
        score={deal.readinessScore}
        ventureLabel={deal.scoreEvent?.composite?.ventureLabel}
        size={size}
      />
    </AnimatedPressable>
  );

  // List mode - collapsible full-width rows
  if (layout === 'list') {
    return (
      <Animated.View
        entering={FadeIn.delay(50 + index * 20).duration(200)}
      >
        <View
          className="rounded-xl overflow-hidden"
          style={{
            backgroundColor: colors.surface.primary,
            borderWidth: isSelected || isInComparison ? 2 : 1,
            borderColor: isSelected ? colors.primary[600] : isInComparison ? colors.primary[400] : colors.border.light,
          }}
        >
          {/* Collapsed Header Row */}
          <AnimatedPressable
            onPress={() => onToggleExpanded(deal.id)}
            onLongPress={() => !isSelectionMode && onEnterSelectionMode()}
            accessibilityRole="button"
            accessibilityLabel={`${deal.startup.company.name}, ${deal.startup.company.industry}, score ${deal.readinessScore}. ${isExpanded ? 'Collapse' : 'Expand'} details`}
            className="p-3 flex-row items-center"
          >
            {selectionCheckbox}
            <View style={{ marginRight: 10 }}>
              <CompanyAvatar
                name={deal.startup.company.name}
                logoUrl={deal.startup.company.logoUrl}
                isKnockout={deal.startup.company.logoIsKnockout}
              />
            </View>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={{ color: colors.text.primary }} className="font-semibold" numberOfLines={1}>
                {deal.startup.company.name}
              </Text>
              <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>
                {deal.startup.company.industry} {'\u2022'} {STAGE_LABELS[deal.startup.company.stage]}
              </Text>
            </View>
            {/* Only the ICON controls ride the identity row.
                The score pill and status badge used to sit here too. Together
                they are ~195pt of unshrinkable content, which on a 393pt screen
                left about 46pt for the company name — production rendered
                "HEL..." for a name of eleven characters (2026-08-09). The two
                badges now sit on their own line below, so nothing is removed and
                the name gets the width back. `flexShrink: 0` is still right:
                icons must not squash. */}
            <View className="flex-row items-center gap-1.5" style={{ marginLeft: 8, flexShrink: 0 }}>
              <AnimatedPressable
                onPress={(e) => { e.stopPropagation(); if (isSaved) { onUnsave(deal.id); } else { onSave('startup', deal.id); } }}
                aria-label={isSaved ? `Remove ${deal.startup.company.name} from saved` : `Save ${deal.startup.company.name}`}
                hitSlop={6}
                pressScale={0.9}
              >
                {isSaved
                  ? <BookmarkCheck size={18} color={colors.primary[600]} fill={colors.primary[600]} />
                  : <Bookmark size={18} color={colors.text.tertiary} />}
              </AnimatedPressable>
              <AnimatedPressable
                onPress={(e) => { e.stopPropagation(); if (isInComparison) { onRemoveFromComparison(deal.id); } else if (canAddMore) { onAddToComparison(deal.id); } }}
                aria-label={isInComparison ? `Remove ${deal.startup.company.name} from comparison` : `Add ${deal.startup.company.name} to comparison`}
                hitSlop={6}
                pressScale={0.9}
              >
                <Scale size={18} color={isInComparison ? colors.primary[600] : colors.text.tertiary} />
              </AnimatedPressable>
              <CardExpandChevron expanded={isExpanded} />
            </View>
          </AnimatedPressable>

          {/* Score + status, moved off the identity row above so the company
              name is not crushed. `flex-wrap` so a long venture label drops to a
              second line instead of overflowing the card. */}
          <View className="flex-row items-center flex-wrap gap-1.5 px-3 pb-3 -mt-1">
            {scoreButton()}
            <StatusBadge
              label={status.label}
              color={status.color}
              backgroundColor={status.bg}
            />
          </View>

          {/* Expanded Content */}
          {isExpanded && (
            <View className="px-3 pb-3 border-t" style={{ borderColor: colors.border.light }}>
              <Text style={{ color: colors.text.secondary }} className="text-sm mt-3 mb-3">
                {deal.startup.company.tagline}
              </Text>

              {/* Match Score */}
              {matchScore && matchScore.overall >= 60 && (
                <View className="mb-3">
                  <View
                    className="flex-row items-center gap-1 px-2 py-1 rounded-full self-start"
                    style={{ backgroundColor: colors.traffic.greenLight }}
                  >
                    <Star size={12} color={colors.success} fill={colors.success} />
                    <Text style={{ color: colors.success }} className="text-xs font-medium">
                      {matchScore.overall}% match {'\u2022'} {matchScore.reasons[0]}
                    </Text>
                  </View>
                </View>
              )}

              {/* Info Tags */}
              <View className="flex-row flex-wrap gap-2 mb-3">
                <View className="px-2 py-1 rounded-lg flex-row items-center gap-1" style={{ backgroundColor: colors.surface.secondary }}>
                  <MapPin size={12} color={colors.text.secondary} />
                  <Text style={{ color: colors.text.secondary }} className="text-xs">{(deal.startup.company.location && deal.startup.company.location !== '.') ? deal.startup.company.location.split(',')[0] : 'Unknown'}</Text>
                </View>
                {(deal.startup.funding.currentRaise?.amount ?? 0) > 0 && (
                  <View className="px-2 py-1 rounded-lg" style={{ backgroundColor: colors.surface.secondary }}>
                    <Text style={{ color: colors.text.secondary }} className="text-xs">{formatCurrency(deal.startup.funding.currentRaise!.amount)}</Text>
                  </View>
                )}
                {dataRoomOpen(deal.status) && (
                  <View className="flex-row items-center gap-1 px-2 py-1 rounded-lg" style={{ backgroundColor: `${colors.success}20` }}>
                    <FolderOpen size={12} color={colors.success} />
                    <Text style={{ color: colors.success }} className="text-xs font-medium">Data Room</Text>
                  </View>
                )}
                {commitmentBadge}
              </View>

              {/* Actions are DERIVED from the stage. Do not add a button here — add it to
                  stage.ts's ACTIONS table, or it will be offered at the wrong stage. */}
              <View className="flex-row flex-wrap gap-2">
                {requestState !== 'none' && (
                  <AccessStatePill state={requestState} colors={colors} />
                )}
                {visibleActionsFor(deal.status, requestState).map((action) => {
                  const presentation = CARD_ACTION_PRESENTATION[action];
                  const pending = action === 'request-access' && isRequestingAccess;
                  return (
                    <CardActionButton
                      key={action}
                      label={pending ? 'Requesting…' : presentation.label}
                      variant={presentation.variant}
                      icon={presentation.icon ? CARD_ACTION_ICONS[presentation.icon] : undefined}
                      disabled={pending}
                      accessibilityLabel={`${presentation.label} — ${deal.startup.company.name}`}
                      onPress={() => runAction(action)}
                    />
                  );
                })}
              </View>
            </View>
          )}
        </View>
      </Animated.View>
    );
  }

  // Compact mode - simple single-line items
  if (layout === 'compact') {
    return (
      <Animated.View
        entering={FadeIn.delay(50 + index * 20).duration(200)}
      >
        <AnimatedPressable
          onPress={() => onViewStartup(deal.id)}
          onLongPress={() => !isSelectionMode && onEnterSelectionMode()}
          accessibilityRole="button"
          accessibilityLabel={`View ${deal.startup.company.name}, ${deal.startup.company.industry}, score ${deal.readinessScore}`}
          className="rounded-xl p-3 flex-row items-center"
          style={{
            backgroundColor: colors.surface.primary,
            borderWidth: isSelected || isInComparison ? 2 : 1,
            borderColor: isSelected ? colors.primary[600] : isInComparison ? colors.primary[400] : colors.border.light,
          }}
        >
          {selectionCheckbox}
          <View style={{ marginRight: 10 }}>
            <CompanyAvatar
              name={deal.startup.company.name}
              logoUrl={deal.startup.company.logoUrl}
                isKnockout={deal.startup.company.logoIsKnockout}
            />
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={{ color: colors.text.primary }} className="font-medium" numberOfLines={1}>
              {deal.startup.company.name}
            </Text>
            <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>
              {deal.startup.company.industry} {'\u2022'} {STAGE_LABELS[deal.startup.company.stage]}
            </Text>
          </View>
          <View className="flex-row items-center gap-1.5" style={{ marginLeft: 8, flexShrink: 0 }}>
            {scoreButton()}
            <StatusBadge
              label={status.label}
              color={status.color}
              backgroundColor={status.bg}
            />
            <ChevronRight size={16} color={colors.text.tertiary} />
          </View>
        </AnimatedPressable>
      </Animated.View>
    );
  }

  // Grid mode - traditional cards
  return (
    <Animated.View
      entering={FadeIn.delay(100 + index * 30).duration(200)}
      style={layout === 'grid' ? (isDesktop ? webStyle({}, { width: 'calc(50% - 6px)' }) : { width: '100%' }) : {}}
    >
      <AnimatedPressable
        onPress={() => onViewStartup(deal.id)}
        onLongPress={() => !isSelectionMode && onEnterSelectionMode()}
        accessibilityRole="button"
        accessibilityLabel={`View ${deal.startup.company.name}, ${deal.startup.company.industry}, score ${deal.readinessScore}`}
        className="rounded-2xl overflow-hidden"
        style={{
          backgroundColor: colors.surface.primary,
          borderWidth: isSelected || isInComparison ? 2 : 0,
          borderColor: isSelected ? colors.primary[600] : isInComparison ? colors.primary[400] : 'transparent',
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.08,
          shadowRadius: 4,
          elevation: 3,
        }}
      >
        <View className="p-4">
          {/* Header Row */}
          <View className="flex-row items-start justify-between mb-3">
            <View className="flex-row items-center gap-3 flex-1">
              {isSelectionMode && (
                <AnimatedPressable
                  onPress={() => onToggleSelection(deal.id)}
                  aria-label={`Select ${deal.startup.company.name}`}
                  accessibilityState={{ checked: isSelected }}
                >
                  {isSelected ? (
                    <CheckSquare size={24} color={colors.primary[600]} />
                  ) : (
                    <Square size={24} color={colors.text.tertiary} />
                  )}
                </AnimatedPressable>
              )}
              <CompanyAvatar
                name={deal.startup.company.name}
                logoUrl={deal.startup.company.logoUrl}
                isKnockout={deal.startup.company.logoIsKnockout}
                size={56}
                radius={12}
              />
              <View className="flex-1">
                <Text style={{ color: colors.text.primary }} className="font-semibold text-base">
                  {deal.startup.company.name}
                </Text>
                <Text style={{ color: colors.text.secondary }} className="text-sm" numberOfLines={1}>
                  {deal.startup.company.tagline}
                </Text>
              </View>
            </View>
            <View className="flex-row items-center gap-2">
              <StatusBadge
                label={status.label}
                color={status.color}
                backgroundColor={status.bg}
                size="md"
              />
              {commitmentBadge}
              <AnimatedPressable
                onPress={(e) => { e.stopPropagation(); if (isSaved) { onUnsave(deal.id); } else { onSave('startup', deal.id); } }}
                aria-label={isSaved ? `Remove ${deal.startup.company.name} from saved` : `Save ${deal.startup.company.name}`}
                hitSlop={6}
                pressScale={0.9}
              >
                {isSaved
                  ? <BookmarkCheck size={20} color={colors.primary[600]} fill={colors.primary[600]} />
                  : <Bookmark size={20} color={colors.text.tertiary} />}
              </AnimatedPressable>
              <AnimatedPressable
                onPress={(e) => { e.stopPropagation(); if (isInComparison) { onRemoveFromComparison(deal.id); } else if (canAddMore) { onAddToComparison(deal.id); } }}
                aria-label={isInComparison ? `Remove ${deal.startup.company.name} from comparison` : `Add ${deal.startup.company.name} to comparison`}
                hitSlop={6}
                pressScale={0.9}
              >
                <Scale size={20} color={isInComparison ? colors.primary[600] : colors.text.tertiary} />
              </AnimatedPressable>
              <ChevronRight size={18} color={colors.text.tertiary} />
            </View>
          </View>

          {/* Match Score Badge */}
          {matchScore && matchScore.overall >= 60 && (
            <View className="mb-3">
              <View
                className="flex-row items-center gap-1 px-2 py-1 rounded-full self-start"
                style={{ backgroundColor: colors.traffic.greenLight }}
              >
                <Star size={12} color={colors.success} fill={colors.success} />
                <Text style={{ color: colors.success }} className="text-xs font-medium">
                  {matchScore.overall}% match
                </Text>
                {matchScore.reasons[0] && (
                  <Text style={{ color: colors.success }} className="text-xs">
                    {'\u2022'} {matchScore.reasons[0]}
                  </Text>
                )}
              </View>
            </View>
          )}

          {/* Info Tags */}
          <View className="flex-row flex-wrap gap-2 mb-3">
            <View
              className="px-2 py-1 rounded-lg flex-row items-center gap-1"
              style={{ backgroundColor: colors.surface.secondary }}
            >
              <MapPin size={12} color={colors.text.secondary} />
              <Text style={{ color: colors.text.secondary }} className="text-xs">
                {(deal.startup.company.location && deal.startup.company.location !== '.') ? deal.startup.company.location.split(',')[0] : 'Unknown'}
              </Text>
            </View>
            <View
              className="px-2 py-1 rounded-lg"
              style={{ backgroundColor: colors.surface.secondary }}
            >
              <Text style={{ color: colors.text.secondary }} className="text-xs">
                {deal.startup.company.industry}
              </Text>
            </View>
            <View
              className="px-2 py-1 rounded-lg"
              style={{ backgroundColor: colors.surface.secondary }}
            >
              <Text style={{ color: colors.text.secondary }} className="text-xs">
                {STAGE_LABELS[deal.startup.company.stage]}
              </Text>
            </View>
            {(deal.startup.funding.currentRaise?.amount ?? 0) > 0 && (
              <View
                className="px-2 py-1 rounded-lg"
                style={{ backgroundColor: colors.surface.secondary }}
              >
                <Text style={{ color: colors.text.secondary }} className="text-xs">
                  {formatCurrency(deal.startup.funding.currentRaise!.amount)}
                </Text>
              </View>
            )}
          </View>

          {/* Score & Actions */}
          <View
            className="flex-row items-center justify-between pt-3 border-t"
            style={{ borderColor: colors.border.light }}
          >
            <View className="flex-row items-center gap-4">
              {scoreButton('md')}
              {dataRoomOpen(deal.status) && (
                <View
                  className="flex-row items-center gap-1 px-2 py-1 rounded-full"
                  style={{ backgroundColor: `${colors.success}20` }}
                >
                  <FolderOpen size={12} color={colors.success} />
                  <Text style={{ color: colors.success }} className="text-xs font-medium">
                    Data Room
                  </Text>
                </View>
              )}
            </View>
          </View>

          {/* Notes */}
          {deal.notes && (
            <View
              className="mt-3 pt-3 border-t"
              style={{ borderColor: colors.border.light }}
            >
              <Text style={{ color: colors.text.tertiary }} className="text-xs italic">
                &quot;{deal.notes}&quot;
              </Text>
            </View>
          )}
        </View>

        {/* Action Bar — same CardActionButton primitive as the list layout.
            Actions are DERIVED from the stage. Do not add a button here — add
            it to stage.ts's ACTIONS table, or it will be offered at the wrong
            stage. `stopPropagation` is required here (unlike the list layout)
            because the whole grid card is itself a Pressable. */}
        <View className="px-4 pb-4 pt-1 border-t" style={{ borderColor: colors.border.light }}>
          <View className="flex-row flex-wrap gap-2">
            {requestState !== 'none' && (
              <AccessStatePill state={requestState} colors={colors} />
            )}
            {visibleActionsFor(deal.status, requestState).map((action) => {
              const presentation = CARD_ACTION_PRESENTATION[action];
              const pending = action === 'request-access' && isRequestingAccess;
              return (
                <CardActionButton
                  key={action}
                  label={pending ? 'Requesting…' : presentation.label}
                  variant={presentation.variant}
                  icon={presentation.icon ? CARD_ACTION_ICONS[presentation.icon] : undefined}
                  stopPropagation
                  disabled={pending}
                  accessibilityLabel={`${presentation.label} — ${deal.startup.company.name}`}
                  onPress={() => runAction(action)}
                />
              );
            })}
          </View>
        </View>
      </AnimatedPressable>
    </Animated.View>
  );
}
